import Head from 'next/head'
import Script from 'next/script'
import { useEffect } from 'react';
import { getRequestJSON } from '../../../api/index/location-api';
import { getArticleDateTime, reviewedIso } from '../../../utils/article-date-time';
import { PartTimeHelper } from '../../../components/index/service-category/domestic-service/PartTimeHelper';
import { helperCostCalculation, helperOneOffRows, helperRateSummary } from '../../../js/part-time-helper-cost';
import { helperTestimonials } from '../../../data/CustomerReviewData';
import { buildBreadcrumb } from 'utils/breadcrumb'
import { buildServiceHubGraph } from 'utils/service-hub-schema'

const HUB = '/sg/part-time-cleaner-singapore'
const PAGE_URL = `https://www.stringssg.com${HUB}`
const OG_IMAGE = "https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/part-time-helper/part-time-helper-in-gray-uniform-living.jpg"

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-28'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {
  const { month, year } = getArticleDateTime();
  const modifiedDate = reviewedIso(CONTENT_REVIEWED);

  const countryCd = "sg"
  const countryName = "Singapore"

  const allRequestCategorySettings = await getRequestJSON(countryName)
  const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Domestic Services")

  const currencyCd = "SGD"

  let helperService = []
  let babySitterService = []
  let elderlyCareService = []
  let cookingAssistanceService = []

  helperCostCalculation(currencyCd, helperService, babySitterService, elderlyCareService, cookingAssistanceService)

  return {
      props: {
        requestCategorySettings,
        countryName,
        countryCd,
        currencyCd,
        helperService,
        elderlyCareService,
        modifiedDate,
        month,
        year,
        helperTestimonials,
      },
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
  const {
    requestCategorySettings,
    countryName,
    currencyCd,
    helperService,
    elderlyCareService,
    modifiedDate,
    month,
    year,
  } = props
  const testimonials = props.helperTestimonials

  // Every price on the page (title, description, rate table, packages, FAQ and
  // the offers below) comes from these calculator rows. The old title claimed
  // "$19/hr", which no booking actually cost.
  const { fromPerHour, minHours, maxHours } = helperRateSummary(helperService)
  const title = `Part Time Cleaner Singapore | Maid & Helper From $${fromPerHour}/hr`
  const description = `Part-time cleaners & maids in Singapore from $${fromPerHour}/hr, ${minHours}-${maxHours} hrs per visit. One-off, weekly or fortnightly. Background-verified, same helper each visit.`

  const schema = buildServiceHubGraph({
    pageUrl: PAGE_URL,
    pageName: title,
    description,
    trail: buildBreadcrumb(HUB),
    items: [
      ...helperService.map((row) => ({ itemName: `Part-time cleaner, ${row.unit} hours, weekly or fortnightly`, serviceCost: Number(row.rate) })),
      ...helperOneOffRows().map((row) => ({ itemName: `Part-time cleaner, ${row.unit} hours, one-off`, serviceCost: Number(row.rate) })),
    ],
    serviceName: 'Part Time Cleaner Singapore',
    serviceType: 'Part-Time Home Cleaning',
    image: OG_IMAGE,
    datePublished: '2025-01-01T07:30:46+00:00',
    modifiedDate,
    // The page embeds this video in the scope section (LazyYouTubeEmbed). The
    // old markup called it "Aircon Servicing Singapore" and gave it a view count
    // copied from another video.
    video: {
      name: 'Part-Time Helper Service in Singapore - StringsSG',
      description: 'What is included in a part-time cleaning visit by a StringsSG helper in Singapore.',
      youtubeId: 'JmlMQDN_V3s',
      thumbnailUrl: 'https://i.ytimg.com/vi/JmlMQDN_V3s/hqdefault.jpg',
      uploadDate: '2023-10-03T08:00:00+08:00',
      duration: 'PT1M0S',
    },
  })

  useEffect(() => {
      const currentLocation = globalThis.location.href;
      if (currentLocation.includes("#")){
          let locationId = currentLocation.substring(currentLocation.indexOf("#") + 1);
          let ele = document.getElementById(locationId);
          if(ele){
              setTimeout(() => {
                  ele.scrollIntoView({ behavior: "smooth", block:"start"});
              }, 500)
          }
      }
  }, [])

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={description}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={PAGE_URL} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={title}/>
        <meta property="og:description" content={description}/>
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="og:image"  content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={PAGE_URL}/>
        <meta name="twitter:title" content={title}/>
        <meta name="twitter:description" content={description}/>
        <meta name="twitter:image" content={OG_IMAGE} /><link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
        <link rel="canonical" href={PAGE_URL}/>
      </Head>
        <PartTimeHelper requestCategorySettings={requestCategorySettings} countryName={countryName} helperService={helperService} elderlyCareService={elderlyCareService}
            currencyCd={currencyCd} testimonials={testimonials} year={year} month={month}/>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
        />
        {/* The Facebook pixel is mounted globally in _app.js with the same id, so
            the copy that used to be here only re-fired init/PageView. */}
    </>
  )
}
